import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { isRefereeAccount } from "../../auth/referee";
import {
  CHOOSE_MODE_PATH,
  canChooseAccountMode,
  homePathForMode,
  staffModeLabel,
  writePreferredMode,
} from "../../auth/accountMode";
import type { RoleCode } from "../../lib/api";
import useFixedChromeHeights from "../../lib/useFixedChromeHeights";
import useVisualViewportChromeShift from "../../lib/useVisualViewportChromeShift";
import GlobalToasts from "../GlobalToasts";
import Icon from "../icons";
import MyAccountDialog from "../MyAccountDialog";
import Ayuda123Widget from "../Ayuda123Widget";
import AdminTournamentContextNav from "../AdminTournamentContextNav";
import UpdatePrompt from "../pwa/UpdatePrompt";
import OfflineBanner from "../pwa/OfflineBanner";
import InstallPrompt from "../pwa/InstallPrompt";
import EnablePushCard from "../pwa/EnablePushCard";

interface NavLinkSpec {
  to: string;
  label: string;
  icon: string;
  roles?: RoleCode[] | "anonymous";
  /** Se oculta si el usuario tiene alguno de estos roles. */
  hideForRoles?: RoleCode[];
}

const NAV_LINKS: NavLinkSpec[] = [
  { to: "/login", label: "Login", icon: "log-in", roles: "anonymous" },
  { to: "/superadmin", label: "Superadmin", icon: "settings", roles: ["superadmin"] },
  { to: "/club", label: "Club", icon: "court", roles: ["club"] },
  { to: "/admin", label: "Admin", icon: "home", roles: ["admin", "superadmin"] },
  { to: "/admin/tournaments", label: "Torneos", icon: "trophy", roles: ["admin", "superadmin"] },
  { to: "/admin/players", label: "Jugadores", icon: "users", roles: ["admin", "superadmin"] },
  { to: "/admin/team", label: "Equipo", icon: "users", roles: ["admin", "superadmin"] },
  { to: "/admin/rankings", label: "Ranking", icon: "tennis", roles: ["admin", "superadmin"] },
  { to: "/admin/calendar", label: "Canchas", icon: "calendar", roles: ["admin", "superadmin"] },
  { to: "/arbitro", label: "Partidos", icon: "clipboard-check", roles: ["admin"] },
  { to: "/admin/notifications", label: "Avisos", icon: "bell", roles: ["admin", "superadmin"] },
  { to: "/admin/finance", label: "Finanzas", icon: "wallet", roles: ["admin", "superadmin"] },
  // Eduardo 2026-10-12: en el menú del admin, «Jugador» se oculta y en su lugar va «Club».
  // Etapa 1 de club: quien ya es staff (rol club) ve «Club» → /club (arriba);
  // el organizador sin club ve la página para crear su club.
  { to: "/club/activar", label: "Club", icon: "court", roles: ["admin", "superadmin"], hideForRoles: ["club"] },
];

const PLAYER_NAV_LINKS: NavLinkSpec[] = [
  { to: "/player", label: "Home", icon: "home", roles: ["player", "admin", "superadmin"] },
  { to: "/player/tournaments", label: "Torneos", icon: "trophy", roles: ["player", "admin", "superadmin"] },
  // «Club» visible otra vez (Eduardo 2026-10-12).
  // «Mi club» del jugador: solo jugadores; el organizador usa su panel de club.
  { to: "/player/clubs", label: "Club", icon: "court", roles: ["player"], hideForRoles: ["admin", "superadmin"] },
  { to: "/player/profile", label: "Mi perfil", icon: "user", roles: ["player", "admin", "superadmin"] },
  { to: "/player/friends", label: "Amigos", icon: "users", roles: ["player", "admin", "superadmin"] },
];

type ThemeMode = "dark" | "light";

const THEME_STORAGE_KEY = "cymergold_tenis_theme";
// Nav superior en móvil (pedido Eduardo 2026-07-25): el menú creció — los
// primeros pills conservan EL ORDEN ACTUAL y el resto vive tras "Más". En
// escritorio se muestran todos (el CSS oculta el botón y los overflow jamás
// se esconden ahí).
const NAV_MOBILE_INLINE_LIMIT = 5;
const TORNEOS123_LOGO_LIGHT_SRC = `${import.meta.env.BASE_URL}torneos123/logo-torneos123-header-on-light.png`;
const TORNEOS123_LOGO_DARK_SRC = `${import.meta.env.BASE_URL}torneos123/logo-torneos123-header-on-dark.png`;

function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedTheme === "light" ? "light" : "dark";
}

export default function AppShell() {
  useFixedChromeHeights();
  // iOS 26 (app instalada): lo fijo se queda ~300pt arriba al bajar tras
  // haber abierto el teclado; ver el hook.
  useVisualViewportChromeShift();
  const { status, user, logout, isImpersonating, stopImpersonation } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState<ThemeMode>(getStoredTheme);
  const [navMoreOpen, setNavMoreOpen] = useState(false);
  const navMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const navMoreSheetRef = useRef<HTMLElement | null>(null);
  const isLightTheme = theme === "light";
  const isPlayerMode = location.pathname === "/player" || location.pathname.startsWith("/player/");
  const isPublicTorneos123Route =
    location.pathname === "/" ||
    location.pathname === "/torneos123" ||
    location.pathname === "/torneos" ||
    location.pathname === "/organizador" ||
    location.pathname === "/jugador" ||
    location.pathname === "/aviso-de-privacidad" ||
    // Alias que redirigen a /organizador: sin el cascarón de la app mientras
    // redirigen (evita el doble encabezado de /clubes).
    location.pathname === "/pricing" ||
    location.pathname === "/clubes" ||
    location.pathname === "/demo";
  const isPublicRegistrationRoute = location.pathname.startsWith("/inscripcion/");
  const isRoleHome = ["/player", "/admin", "/club", "/superadmin"].includes(location.pathname);
  // Organizador que también juega (Eduardo 2026-10-04): botón visible para
  // pasar de un lado al otro. No sale en «Ver como jugador» (esa sesión es
  // del jugador) ni en la pantalla donde se elige al entrar.
  const modeSwitchUser =
    status === "authenticated" &&
    !isImpersonating &&
    !isPublicRegistrationRoute &&
    location.pathname !== CHOOSE_MODE_PATH &&
    canChooseAccountMode(user)
      ? user
      : null;
  const modeSwitchLabel = modeSwitchUser
    ? isPlayerMode
      ? `modo ${staffModeLabel(modeSwitchUser)}`
      : "modo jugador"
    : "";
  // Versión corta para el header fijo (≤760px): solo el lado al que se va.
  const modeSwitchShortLabel = modeSwitchUser
    ? isPlayerMode
      ? staffModeLabel(modeSwitchUser).charAt(0).toUpperCase() + staffModeLabel(modeSwitchUser).slice(1)
      : "Jugador"
    : "";
  const isAdminOnPublicRegistrationRoute =
    isPublicRegistrationRoute &&
    status === "authenticated" &&
    !!user &&
    (user.roles.includes("admin") || user.roles.includes("superadmin"));

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const navLinks = isPlayerMode ? PLAYER_NAV_LINKS : NAV_LINKS;
  const visibleLinks = navLinks.filter((link) => {
    if (link.roles === "anonymous") {
      return status !== "authenticated";
    }
    if (!link.roles) return true;
    if (!user) return false;
    if (link.hideForRoles?.some((role) => user.roles.includes(role))) return false;
    // Árbitro (2026-10-16): solo «Mis partidos» y sus avisos.
    const referee = isRefereeAccount(user);
    if (link.to === "/arbitro") return referee;
    if (referee) return link.to === "/admin/notifications";
    // Con el botón de cambio de modo, la pastilla «Jugador» sobra.
    if (modeSwitchUser && !isPlayerMode && link.to === "/player") return false;
    return link.roles.some((role) => user.roles.includes(role));
  });
  // Overflow del nav móvil: el ORDEN no cambia — los que no caben pasan al
  // panel de "Más" tal cual vienen.
  const overflowNavLinks = visibleLinks.slice(NAV_MOBILE_INLINE_LIMIT);
  const navMoreActive = overflowNavLinks.some(
    (link) => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`),
  );

  useEffect(() => {
    // Navegar (o cambiar de modo) cierra el panel de "Más".
    setNavMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Panel de "Más" abierto (2º gate #138): Escape lo cierra y DEVUELVE el
    // foco al botón (semántica de disclosure); y si el viewport crece a
    // escritorio, se cierra solo — el CSS además lo oculta por media rule
    // como cinturón.
    if (!navMoreOpen) return;
    // Foco al PRIMER item al abrir (2º gate #139): el panel vive portalizado
    // al final de body, así que Tab desde el botón no llegaría solo.
    const firstItem = navMoreSheetRef.current?.querySelector<HTMLElement>("a, button");
    firstItem?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setNavMoreOpen(false);
        navMoreButtonRef.current?.focus();
      }
    };
    const desktopMedia = window.matchMedia("(min-width: 641px)");
    const onMediaChange = () => {
      if (desktopMedia.matches) setNavMoreOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    desktopMedia.addEventListener("change", onMediaChange);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      desktopMedia.removeEventListener("change", onMediaChange);
    };
  }, [navMoreOpen]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  async function handleStopImpersonation() {
    await stopImpersonation();
    // Route to "/" and let RootRedirect send the restored user to their own home
    // (superadmin → /superadmin, admin → /admin), rather than forcing /admin/players.
    navigate("/", { replace: true });
  }

  function handleSwitchMode() {
    if (!modeSwitchUser) return;
    const nextMode = isPlayerMode ? "staff" : "player";
    writePreferredMode(nextMode);
    navigate(homePathForMode(modeSwitchUser, nextMode));
  }

  function handleToggleTheme() {
    setTheme((currentTheme) => (currentTheme === "light" ? "dark" : "light"));
  }

  function handlePublicRegistrationBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/admin/tournaments", { replace: true });
  }

  const shellContent = isPublicTorneos123Route ? (
    <>
      <Outlet />
      <GlobalToasts />
    </>
  ) : (
    <div className={`app-shell${isPublicRegistrationRoute ? " app-shell--public-registration" : ""}${isPlayerMode ? " app-shell--player-mode" : ""}`}>
      <header className={`app-header${modeSwitchUser ? " app-header--with-mode" : ""}`}>
        <div className={`app-header__brand${modeSwitchUser ? " app-header__brand--with-mode" : ""}`}>
          <img className="app-header__brand-logo" src={isLightTheme ? TORNEOS123_LOGO_LIGHT_SRC : TORNEOS123_LOGO_DARK_SRC} alt="Torneos123" />
          {modeSwitchUser && (
            <button
              type="button"
              className="app-header__logout app-header__mode-switch"
              onClick={handleSwitchMode}
              aria-label={`Ir a ${modeSwitchLabel}`}
              title={`Ir a ${modeSwitchLabel}`}
            >
              <span className="app-nav__icon" aria-hidden="true">
                <Icon name={isPlayerMode ? "settings" : "tennis"} />
              </span>
              {/* Con el header fijo (≤760px) va la versión corta («Jugador»). */}
              <span className="app-header__mode-switch-long">Ir a {modeSwitchLabel}</span>
              <span className="app-header__mode-switch-short" aria-hidden="true">
                {modeSwitchShortLabel}
              </span>
            </button>
          )}
          {status === "authenticated" && !isImpersonating && (
            <button
              type="button"
              className="app-header__logout app-header__account"
              onClick={() => setAccountOpen(true)}
              aria-label="Mi cuenta"
              title="Mi cuenta"
            >
              <span className="app-nav__icon" aria-hidden="true">
                <Icon name="user" />
              </span>
              <span className="app-nav__label">Mi cuenta</span>
            </button>
          )}
          {accountOpen && <MyAccountDialog onClose={() => setAccountOpen(false)} />}
          {/* Oculto en impersonación: el backend rechaza cambiar la
              contraseña de un jugador desde el modo "ver como jugador". */}
          {status === "authenticated" && !isImpersonating && (
            <NavLink
              to="/cuenta/contrasena"
              className="app-header__logout app-header__password"
              aria-label="Cambiar contraseña"
              title="Cambiar contraseña"
            >
              <span className="app-nav__icon" aria-hidden="true">
                <Icon name="lock" />
              </span>
              <span className="app-nav__label">Contraseña</span>
            </NavLink>
          )}
          {status === "authenticated" && (
            <button
              type="button"
              className="app-header__logout"
              onClick={handleLogout}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <span className="app-nav__icon" aria-hidden="true">
                <Icon name="power" />
              </span>
              <span className="app-nav__label">Salir</span>
            </button>
          )}
        </div>
        <nav className="app-nav" aria-label={isPublicRegistrationRoute ? "Navegación de inscripción pública" : "Main navigation"}>
          {isPublicRegistrationRoute ? (
            isAdminOnPublicRegistrationRoute ? (
              <button
                type="button"
                className="public-registration-admin-back"
                onClick={handlePublicRegistrationBack}
                aria-label="Regresar al administrador"
                title="Regresar al administrador"
              >
                <span className="app-nav__icon" aria-hidden="true">
                  <Icon name="arrow-left" />
                </span>
                <span className="app-nav__label">Regresar</span>
              </button>
            ) : null
          ) : (
            <>
              {visibleLinks.map((link, index) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    [
                      index >= NAV_MOBILE_INLINE_LIMIT ? "app-nav__link--overflow" : "",
                      isActive ? "active" : "",
                    ].filter(Boolean).join(" ") || undefined}
                  end={link.to === "/player"}
                  aria-label={link.label}
                  title={link.label}
                >
                  <span className="app-nav__icon" aria-hidden="true">
                    <Icon name={link.icon} />
                  </span>
                  <span className="app-nav__label">{link.label}</span>
                </NavLink>
              ))}
              {overflowNavLinks.length > 0 && (
                <button
                  ref={navMoreButtonRef}
                  type="button"
                  className={`app-nav__more${navMoreActive ? " active" : ""}${navMoreOpen ? " app-nav__more--open" : ""}`}
                  onClick={() => setNavMoreOpen((open) => !open)}
                  aria-expanded={navMoreOpen}
                  aria-controls="app-nav-more-sheet"
                  aria-label="Más opciones del menú"
                  title="Más"
                >
                  <span className="app-nav__icon" aria-hidden="true">
                    <Icon name="more" />
                  </span>
                  <span className="app-nav__label">Más</span>
                </button>
              )}
              <button
                type="button"
                className={`theme-toggle${overflowNavLinks.length > 0 ? " app-nav__link--overflow" : ""}`}
                onClick={handleToggleTheme}
                aria-label={isLightTheme ? "Cambiar a tema oscuro" : "Cambiar a tema claro"}
                title={isLightTheme ? "Cambiar a tema oscuro" : "Cambiar a tema claro"}
              >
                <span className="theme-toggle__icon" aria-hidden="true">
                  <Icon name={isLightTheme ? "sun" : "moon"} />
                </span>
                <span className="app-nav__label">{isLightTheme ? "Claro" : "Oscuro"}</span>
              </button>
            </>
          )}
        </nav>
        {/* PORTAL a body (reporte Eduardo 2026-07-26: "Más" se trababa):
            .app-header tiene backdrop-filter, que lo convierte en containing
            block de position:fixed — dentro del header, el sheet quedaba
            CLIPEADO por su overflow:hidden móvil (panel invisible) y el
            backdrop cubría la barra entera bloqueando todos los taps. Fuera
            del header, fixed vuelve a ser relativo al viewport. */}
        {navMoreOpen && overflowNavLinks.length > 0 && createPortal(
          <>
            <div
              className="app-nav-more-backdrop"
              onClick={() => {
                // Cerrar sin navegar devuelve el foco al botón (igual que
                // Escape); al navegar desde un item, el foco sigue a la ruta.
                setNavMoreOpen(false);
                navMoreButtonRef.current?.focus();
              }}
              aria-hidden="true"
            />
            <nav ref={navMoreSheetRef} id="app-nav-more-sheet" className="app-nav-more-sheet" aria-label="Más opciones del menú">
              {overflowNavLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) => `app-nav-more-sheet__item${isActive ? " active" : ""}`}
                  end={link.to === "/player"}
                  onClick={() => setNavMoreOpen(false)}
                >
                  <span className="app-nav__icon" aria-hidden="true">
                    <Icon name={link.icon} />
                  </span>
                  <span>{link.label}</span>
                </NavLink>
              ))}
              <button
                type="button"
                className="app-nav-more-sheet__item"
                onClick={() => {
                  handleToggleTheme();
                  setNavMoreOpen(false);
                  navMoreButtonRef.current?.focus();
                }}
              >
                <span className="app-nav__icon" aria-hidden="true">
                  <Icon name={isLightTheme ? "sun" : "moon"} />
                </span>
                <span>{isLightTheme ? "Tema oscuro" : "Tema claro"}</span>
              </button>
            </nav>
          </>,
          document.body,
        )}
      </header>
      {!isPlayerMode && !isPublicRegistrationRoute && <AdminTournamentContextNav />}
      {isImpersonating && user && (
        <div className="impersonation-banner" role="status">
          <span>
            Viendo sistema como{" "}
            {user.roles.includes("admin") || user.roles.includes("superadmin")
              ? "administrador"
              : "jugador"}
            : <strong>{user.full_name}</strong>
          </span>
          <button type="button" className="btn btn--compact" onClick={handleStopImpersonation}>
            Volver a mi sesión
          </button>
        </div>
      )}
      <main className="app-main">
        {status === "authenticated" && isRoleHome ? (
          <>
            <InstallPrompt />
            <EnablePushCard />
          </>
        ) : null}
        <Outlet />
      </main>
      <GlobalToasts />
      <Ayuda123Widget />
      <footer className="app-footer">Torneos123 · Compite. Progresa. Gana.</footer>
    </div>
  );

  return (
    <>
      <UpdatePrompt />
      <OfflineBanner />
      {shellContent}
    </>
  );
}
